import React, { useState, VFC } from 'react';

import { AxiosInstance } from 'axios';
import { noop } from 'lodash';
import { Button } from 'mintel-react-ui';

import { cwaUrl } from '/configuration';
import { CustomPanelType, PublishStatus } from '/types';
import { buildArticleApiRelativeUrl } from '/utils/cwaAxios';
import useTranslate, { Trans } from '/utils/translations';

import styles from './LongTermPreviewPanel.scss';

type LongTermPreviewPanelProps = {
  cwaAxios: AxiosInstance;
  isLongTermPreview: boolean;
  articleId: string;
  trackLongTermPreview: (action: string) => void;
};

const LongTermPreviewPanelComponent: VFC<LongTermPreviewPanelProps> = ({
  cwaAxios,
  isLongTermPreview,
  articleId,
  trackLongTermPreview,
}) => {
  const t = useTranslate();
  const [active, setActive] = useState(true);

  const contentLocation = {
    publishStatus: 'long_term_preview' as PublishStatus,
    contentType: 'article',
    slugOrId: articleId,
  };

  const handleClick = async (): Promise<void> => {
    setActive(false);
    const requestMethod = isLongTermPreview ? 'delete' : 'post';
    trackLongTermPreview(requestMethod);
    await cwaAxios[requestMethod](buildArticleApiRelativeUrl(contentLocation));
    if (isLongTermPreview) {
      window.location.href = cwaUrl; // Redirect after successful deletion
    }
  };

  const Btn = isLongTermPreview ? Button.DangerButton : Button.ActionButton;
  return (
    <div className={styles.container}>
      <Btn handleClick={handleClick} disabled={!active}>
        {isLongTermPreview ? t('Delete') : t('Generate')}
      </Btn>
    </div>
  );
};

const LongTermPreviewPanel = (
  cwaAxios: AxiosInstance,
  isLongTermPreview: boolean,
  articleId: string,
  trackLongTermPreview: (action: string) => void,
): CustomPanelType => ({
  children: (
    <LongTermPreviewPanelComponent
      cwaAxios={cwaAxios}
      isLongTermPreview={isLongTermPreview}
      articleId={articleId}
      trackLongTermPreview={trackLongTermPreview}
    />
  ),
  closeSidebar: noop,
  iconName: 'eye',
  color: 'quaternary',
  title: <Trans>Long Term Preview</Trans>,
  subtitle: isLongTermPreview ? (
    <Trans>Delete this Long Term Preview.</Trans>
  ) : (
    <Trans>
      Generate a Long Term Preview for this piece. Long term previews expire
      after a month and are removed once the piece is published.
    </Trans>
  ),
  width: 400,
  id: 'long-term-preview-panel',
});

export default LongTermPreviewPanel;
